import type { BaseItem } from "~/models/Content/BaseItem"
import type { ContentType } from "~/models/Content/ContentType"

interface GetContentOptions<T extends ContentType> extends IbexaQuery<T> {
  childDepth?: number
}

export function useGetContent() {
  const fetchResults = useSearch()

  async function getContent<T extends ContentType>({
    childDepth = 0,
    ...query
  }: GetContentOptions<T>): Promise<MappedIbexaContent<T>[]> {
    try {
      const response = await fetchResults<T>(query)

      const results = response.data

      if (childDepth) {
        return await attachChildren(results, childDepth)
      }

      return results ?? []
    } catch (error) {
      console.error(
        "Rest api request or mapping teh results failed:",
        error,
      )

      captureException(error, {
        extra: {
          query,
        },
      })
      return []
    }
  }

  async function attachChildren<T extends BaseItem>(
    items: T[],
    childDepth: number,
  ): Promise<T[]> {
    if (items.length === 0)
      return items

    // Get all locationIds in one go
    const locationIds = items.map(item => item.locationId)

    // Fetch all children in a single query
    const allChildrenResponse = await getContent({
      childDepth: childDepth - 1,
      parentLocationId: locationIds,
      sortField: "priority",
      sortOrder: "asc",
    })

    // Create a map for quick lookup
    const childrenMap = allChildrenResponse.reduce(
      (acc, child) => {
        if (child.parentLocationId) {
          if (!acc[child.parentLocationId]) {
            acc[child.parentLocationId] = []
          }
          acc[child.parentLocationId]?.push(child)
        }
        return acc
      },
      {} as Record<number, BaseItem[]>,
    )

    // Assign children to their parents
    return items.map(item => ({
      ...item,
      children: childrenMap[item.locationId] ?? [],
    }))
  }

  return getContent
}
